import React, { forwardRef, useRef, useImperativeHandle, useState, useEffect } from "react";
import { SovosBox, SovosDatePicker, SovosFilterTextEntry } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import { FORM_ACTIONS } from "../FormActions";

import styles from "./GeneratorForm.module.sass";
import { fetchApi } from "../../../config/fetchConfig";
import { connect } from "react-redux";
import dateFormat from "dateformat";

const GeneratorForm = (props, ref) => {
    let {
        data,
        file_types,
        file_types_modules,
        projectFiscalYears,
        status,
        onChange,
        onSave,
        requestDates,
        generator_extra_dimension,
        user,
        dispatch,
    } = props;
    const { dateFrom, dateTo } = requestDates || {};
    const { lastProjectFiscalYear: lastFiscalYear, projectFiscalYears: projectFiscalYearsArray } =
        projectFiscalYears || {};
    const [inputsDate, setInputsDate] = useState({});
    const formRef = useRef();
    const { fileTypeModuleId, fileTypeId, endPointToRequest, projectId } = data || {};

    const { i18n } = useGlobalValue();

    const [fileTypesPerLocale, setFileTypesPerLocale] = useState({});
    const [locale, setLocale] = useState("");
    const [currentFileType, setCurrentFileType] = useState("");
    const [fiscalYear, setFiscalYear] = useState(lastFiscalYear);
    const [extraDimensions, setExtraDimensions] = useState({});
    const [filterDimensions, setFilterDimensions] = useState([]);
    const [filterDimensionsForDatasetLocaleFileType, setFilterDimensionsForDatasetLocaleFileType] =
        useState([]);
    const [hasConfiguration, setHasConfiguration] = useState(false);
    const [configuredDimensions, setConfiguredDimensions] = useState({});

    useEffect(() => {
        const tmpFileTypeLocale = {};
        if (file_types) {
            for (const file_type of file_types) {
                if (tmpFileTypeLocale[file_type.dataset_locale]) {
                    tmpFileTypeLocale[file_type.dataset_locale].push(file_type);
                } else {
                    tmpFileTypeLocale[file_type.dataset_locale] = [file_type];
                }
            }
        }
        setFileTypesPerLocale(tmpFileTypeLocale);
    }, [file_types]);

    useEffect(() => {
        if (
            filterDimensions &&
            locale &&
            currentFileType &&
            hasConfiguration &&
            fiscalYear &&
            projectId
        ) {
            const dimensionFilters = filterDimensions.map((elem) => elem.id);
            const queriesBody = {
                query: `query($p:String!,$f:String!,$ids:String!){calculate_dimensions_filters(projectId:$p,fiscalYear:$f,ids:$ids)}`,
                variables: {
                    p: `${projectId}`,
                    f: `${fiscalYear}`,
                    ids: `${dimensionFilters}`,
                },
            };
            fetchApi("/graphql", queriesBody, {}, { dispatch, user })
                .then((res) => res.json())
                .then((json) => {
                    const { data } = json;
                    if (data && data.calculate_dimensions_filters) {
                        setFilterDimensionsForDatasetLocaleFileType(
                            data.calculate_dimensions_filters,
                        );
                    }
                })
                .catch((e) => {
                    setFilterDimensionsForDatasetLocaleFileType([]);
                });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filterDimensions, locale, currentFileType, hasConfiguration, projectId, fiscalYear]);

    useEffect(() => {
        if (
            extraDimensions &&
            extraDimensions[locale] &&
            extraDimensions[locale][currentFileType]
        ) {
            setFilterDimensions(extraDimensions[locale][currentFileType]);
            setHasConfiguration(true);
        }
    }, [locale, currentFileType, extraDimensions]);

    useEffect(() => {
        if (generator_extra_dimension && generator_extra_dimension.length) {
            const extraDimensionsByDatasetAndFileType = generator_extra_dimension.reduce(
                (acc, elem) => {
                    if (!acc[elem.locale]) {
                        acc[elem.locale] = {};
                    }
                    if (!acc[elem.locale][elem.fileType]) {
                        acc[elem.locale][elem.fileType] = [];
                    }
                    acc[elem.locale][elem.fileType].push(elem);
                    return acc;
                },
                {},
            );
            setExtraDimensions(extraDimensionsByDatasetAndFileType);
        }
    }, [generator_extra_dimension]);

    useEffect(() => {
        if (dateFrom && dateTo) {
            setFiscalYear(new Date(dateTo).getFullYear().toString());
            const startDate = new Date(dateFrom);
            const endDate = new Date(dateTo);
            setInputsDate({ startDate, endDate });
        } else if (lastFiscalYear && lastFiscalYear !== "null") {
            setFiscalYear(lastFiscalYear);
            const startDate = new Date().setFullYear(lastFiscalYear);
            const endDate = new Date().setFullYear(lastFiscalYear);
            setInputsDate({ startDate, endDate });
        } else {
            setFiscalYear(new Date().getFullYear().toString());
            const startDate = new Date();
            const endDate = new Date();
            setInputsDate({ startDate, endDate });
        }
    }, [dateTo, lastFiscalYear, dateFrom]);

    const handleChange = (e, prop, value) => {
        onChange &&
            onChange({
                originalEvent: e,
                target: {
                    value: {
                        data: {
                            ...data,
                            [prop]: value,
                        },
                    },
                },
            });
    };

    const handleDateChange = (e, fieldName) => {
        setInputsDate((inputs) => ({
            ...inputs,
            [fieldName]: e,
        }));
    };

    useImperativeHandle(ref, () => ({
        submit: () => {
            formRef.current.dispatchEvent(new Event("submit", { cancelable: true, bubbles: true }));
        },
    }));
    return status !== "loading" ? (
        <form
            ref={formRef}
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (formRef.current.checkValidity()) {
                    data["__typename"] = endPointToRequest || "GeneratorConfiguration";
                    data.fiscalYear = fiscalYear;
                    for (const props in inputsDate) {
                        data[`${props}`] = dateFormat(new Date(inputsDate[props]), "yyyy-mm-dd");
                    }
                    data["configuredDimensions"] = JSON.stringify(configuredDimensions);
                    data["fileTypeModuleId"] = fileTypeModuleId || 0;
                    data["fileTypeId"] = fileTypeId || 0;
                    data["filterDimensionsForDatasetLocaleFileType"] = JSON.stringify(
                        filterDimensionsForDatasetLocaleFileType || [],
                    );
                    onSave && onSave();
                } else {
                    formRef.current.reportValidity();
                }
            }}
        >
            <SovosBox className={styles["flex-container"]}>
                <SovosBox className={styles["flex-child"]}>
                    <SovosDatePicker
                        sx={{ width: "100%" }}
                        label={i18n`Start Date`}
                        format={"yyyy-MM-dd"}
                        value={inputsDate["startDate"] || null}
                        onChange={(e) => {
                            handleDateChange(new Date(e), "startDate");
                        }}
                    />
                </SovosBox>
                <SovosBox className={styles["flex-child"]}>
                    <SovosDatePicker
                        sx={{ width: "100%" }}
                        label={i18n`End Date`}
                        format={"yyyy-MM-dd"}
                        value={inputsDate["endDate"] || null}
                        onChange={(e) => {
                            handleDateChange(new Date(e), "endDate");
                        }}
                    />
                </SovosBox>
            </SovosBox>
            <SovosBox className={styles["input-field"]}>
                <SovosFilterTextEntry
                    sx={{ '& .MuiFilledInput-input': { minHeight: '48px' } }}
                    label={i18n`Fiscal Year`}
                    data={[fiscalYear || ""]}
                    AutocompleteProps={{
                        loading: projectFiscalYearsArray?.length === 0,
                        options: projectFiscalYearsArray || [],
                        getOptionLabel: (option) => option.toString(),
                        isOptionEqualToValue: (option, value) =>
                            option.toString() === value.toString(),
                    }}
                    onChange={(e) => {
                        const [eventValue] = e;
                        setFiscalYear(`${eventValue}` || "");
                    }}
                />
            </SovosBox>
            <SovosBox className={styles["input-field"]}>
                <SovosFilterTextEntry
                    sx={{ '& .MuiFilledInput-input': { minHeight: '48px' } }}
                    label={i18n`Dataset Locale`}
                    data={[
                        fileTypeModuleId && file_types_modules
                            ? file_types_modules.find(({ id }) => id === fileTypeModuleId) ||
                            file_types_modules[0]
                            : { id: "", configSet: "" },
                    ]}
                    AutocompleteProps={{
                        loading: file_types_modules?.length === 0,
                        options: file_types_modules || [],
                        getOptionLabel: (option) => option.configSet,
                        isOptionEqualToValue: (option, value) => option.id === value.id,
                    }}
                    onChange={(e) => {
                        const [eventValue] = e;
                        handleChange(e, "fileTypeModuleId", eventValue.id);
                        const fileTypeModule = file_types_modules.find(
                            ({ id }) => id === eventValue.id,
                        );
                        setLocale(fileTypeModule.configSet || 0);
                    }}
                />
            </SovosBox>
            {locale && (
                <SovosFilterTextEntry
                    sx={{ '& .MuiFilledInput-input': { minHeight: '48px' } }}
                    label={i18n`File Types`}
                    data={[
                        fileTypeId && file_types
                            ? file_types.find(({ id }) => id === fileTypeId) || file_types[0]
                            : { id: "", name: "" },
                    ]}
                    AutocompleteProps={{
                        loading: fileTypesPerLocale?.length === 0,
                        options: fileTypesPerLocale[locale] || [],
                        getOptionLabel: (option) => option.name,
                        isOptionEqualToValue: (option, value) => option.id === value.id,
                    }}
                    onChange={(e) => {
                        setHasConfiguration(false);
                        const [eventValue] = e;
                        handleChange(e, "fileTypeId", eventValue.id);
                        setCurrentFileType(eventValue.name[0]);
                        setFilterDimensionsForDatasetLocaleFileType([]);
                    }}
                />
            )}
            {filterDimensionsForDatasetLocaleFileType &&
                (filterDimensionsForDatasetLocaleFileType || []).map((dimension, index) => {
                    return (
                        <SovosBox key={index} className={styles["input-field"]}>
                            <SovosFilterTextEntry
                                sx={{ '& .MuiFilledInput-input': { minHeight: '48px' } }}
                                label={i18n`${dimension.name}`}
                                data={[
                                    filterDimensionsForDatasetLocaleFileType[index].values.find(
                                        (elem) =>
                                            `${elem.id}` === configuredDimensions[dimension.id],
                                    ) || { id: "", name: "" },
                                ]}
                                AutocompleteProps={{
                                    loading: filterDimensionsForDatasetLocaleFileType?.length === 0,
                                    options: filterDimensionsForDatasetLocaleFileType[index].values
                                        ? filterDimensionsForDatasetLocaleFileType[index].values
                                        : [],
                                    getOptionLabel: (option) => option.name,
                                    isOptionEqualToValue: (option, value) => option.id === value.id,
                                }}
                                onChange={(e) => {
                                    const [eventValue] = e;
                                    setConfiguredDimensions((prevState) => ({
                                        ...prevState,
                                        [`${dimension.id}`]: eventValue.id,
                                    }));
                                }}
                            />
                        </SovosBox>
                    );
                })}
        </form>
    ) : null;
};

const GeneratorFormRef = forwardRef(GeneratorForm);

GeneratorFormRef.queries = (args) => {
    const { projectId } = args || {};
    return [
        {
            resource: "file_types",
            body: "id,name,dataset_locale",
        },
        {
            resource: "file_types_modules",
            body: "id,name, configSet",
            args: {
                projectId,
            },
        },
        {
            resource: "project_fiscal_years",
            args: {
                projectId,
            },
            body: "lastProjectFiscalYear, projectFiscalYears",
        },
        {
            resource: "get_last_request_date_generator_data_files",
            args: {
                projectId,
            },
            body: "dateFrom, dateTo",
        },
        {
            resource: "generator_extra_dimension",
            body: "id,locale,fileType,name",
            args: {
                projectId,
            },
        },
    ].filter((q) => q);
};

GeneratorFormRef.dataMapper = [
    "file_types",
    "file_types_modules",
    "projectFiscalYears",
    "requestDates",
    "generator_extra_dimension",
];

GeneratorFormRef.actions = [FORM_ACTIONS.GENERATE];

export default connect(
    ({ user }) => ({
        user,
    }),
    null,
    null,
    { forwardRef: true },
)(GeneratorFormRef);
